import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<typeof comics>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setCategoryDropdownOpen(false);
  }, [location]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const results = comics.filter(
        (c) =>
          c.title.includes(searchQuery.trim()) ||
          c.author.includes(searchQuery.trim()) ||
          c.description.includes(searchQuery.trim())
      );
      setSearchResults(results);
      setShowSearchResults(true);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (!e.target.value.trim()) {
      setShowSearchResults(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 max-w-[1440px] mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="font-heading text-xl md:text-2xl font-bold text-primary-500">
              可游漫画
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/'
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              首页
            </Link>

            {/* Category Dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setCategoryDropdownOpen(true)}
              onMouseLeave={() => setCategoryDropdownOpen(false)}
            >
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap flex items-center gap-1 ${
                  location.pathname.startsWith('/category')
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${categoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {categoryDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 bg-white rounded-lg border border-background-200/70 py-2 min-w-[160px]">
                  {categories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={`/category/${cat.slug}`}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-150 whitespace-nowrap"
                    >
                      <i className={`${cat.icon} text-base`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                location.pathname === '/about'
                  ? 'text-primary-500 bg-primary-50'
                  : 'text-foreground-700 hover:text-primary-500 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </nav>

          {/* Search Bar */}
          <div className="hidden md:flex items-center relative">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="搜索漫画..."
                className="w-48 lg:w-56 pl-9 pr-3 py-2 text-sm rounded-full bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 placeholder:text-foreground-400"
              />
              <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-500 cursor-pointer">
                <i className="ri-search-line text-sm"></i>
              </button>
            </form>

            {/* Search Results Dropdown */}
            {showSearchResults && (
              <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-xl border border-background-200/70 overflow-hidden">
                <div className="p-3 border-b border-background-100">
                  <p className="text-xs text-foreground-500">
                    找到 {searchResults.length} 部相关漫画
                  </p>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {searchResults.length > 0 ? (
                    searchResults.map((comic) => (
                      <Link
                        key={comic.id}
                        to={`/comic/${comic.id}`}
                        onClick={() => setShowSearchResults(false)}
                        className="flex items-center gap-3 p-3 hover:bg-background-50 transition-colors duration-150"
                      >
                        <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0">
                          <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-800 truncate">{comic.title}</p>
                          <p className="text-xs text-foreground-500">{comic.author}</p>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="p-4 text-sm text-foreground-500 text-center">未找到相关漫画</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
            aria-label="菜单"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70">
          <div className="px-4 py-3">
            <form onSubmit={handleSearch} className="mb-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="搜索漫画..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 focus:outline-none focus:border-primary-300"
                />
                <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 cursor-pointer">
                  <i className="ri-search-line text-sm"></i>
                </button>
              </div>
            </form>
            <nav className="flex flex-col gap-1">
              <Link to="/" className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-150">
                首页
              </Link>
              <p className="px-3 py-2 text-xs font-medium text-foreground-400 uppercase tracking-wider">分类</p>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-150"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
              <Link to="/about" className="px-3 py-2.5 rounded-lg text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50/50 transition-colors duration-150">
                关于我们
              </Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}